import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewEncapsulation,
  afterRender,
  computed,
  inject,
  viewChild,
} from '@angular/core';

import { ChartTooltipService } from './chart-tooltip.service';

/**
 * Surface d'infobulle du rapport — un seul exemplaire, posé par la page.
 *
 * Le markup reprend celui du document d'origine (`#chart-tooltip`) au nom de
 * classe près : la feuille de styles globale du rapport cible ces classes, et
 * l'encapsulation est désactivée pour cette raison.
 *
 * Le texte ne porte jamais la couleur de la série ; c'est la pastille qui
 * identifie, la valeur reste dans les jetons de texte. Décision de la maquette,
 * pour que l'infobulle reste lisible en mode sombre comme à l'impression.
 */
@Component({
  selector: 's2e-chart-tooltip',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.display]': "'contents'" },
  template: `
    <div
      #box
      class="chart-tooltip"
      id="chart-tooltip"
      role="tooltip"
      [hidden]="!tooltip.visible()"
      [style.transform]="transform()"
    >
      <p class="chart-tooltip__title">{{ tooltip.title() }}</p>
      <ul class="chart-tooltip__list">
        @for (item of tooltip.items(); track $index) {
          <li class="chart-tooltip__item">
            <span class="chart-tooltip__label">
              <span
                class="chart-tooltip__swatch"
                [style.background-color]="item.color"
              ></span>{{ item.label }}
            </span>
            <span class="chart-tooltip__value tabular">{{ item.value }}</span>
          </li>
        }
      </ul>
    </div>
  `,
})
export class ChartTooltipComponent {
  readonly tooltip = inject(ChartTooltipService);

  private readonly box = viewChild.required<ElementRef<HTMLElement>>('box');

  readonly transform = computed(
    () => `translate(${this.tooltip.x()}px,${this.tooltip.y()}px)`,
  );

  constructor() {
    /* Mesure après écriture du DOM et avant peinture : c'est le seul moment où
       la boîte correspond au contenu affiché. Écrire la même largeur ne
       déclenche rien (les signaux comparent), donc pas de boucle de rendu. */
    afterRender(() => {
      if (!this.tooltip.visible()) return;
      const element = this.box().nativeElement;
      this.tooltip.measure(element.offsetWidth, element.offsetHeight);
    });

    /* Le survol garde l'infobulle collée au pointeur ; au défilement le
       pointeur ne bouge pas mais le graphique, si — la bulle désignerait alors
       une barre qu'elle ne commente plus. Écouteur passif : ne jamais retarder
       le défilement d'une page qui fait plusieurs milliers de pixels. */
    const onScroll = (): void => this.tooltip.hide();
    window.addEventListener('scroll', onScroll, { passive: true });
    inject(DestroyRef).onDestroy(() => window.removeEventListener('scroll', onScroll));
  }
}
